/**
 * A PICTURE FOR EVERY VEHICLE, never the coin.
 *
 * Anthony, 2026-10-14, on the fleet page: "add the same photo tiles for
 * trucks etc, don't fill with coins, fill with a photo of the vehicle ...
 * or an ai generated one is fine, reuse for similar models if they don't
 * have photos."
 *
 * The desk's tiles already go: the vehicle's own photo, then a picture of
 * its make and model from public/images/cars, then the coin. The middle
 * step only matched a model it had a picture of word for word, so a fleet
 * got coins down the whole column: "GMC Denali" (a trim, not a model),
 * "F-350" (we have the F-250), "Savana" (the Express with a GMC badge),
 * and every Isuzu NPR and Freightliner, because heavy-duty makes were
 * refused outright.
 *
 * So the order is now:
 *
 *   1. that exact make and model                     chevrolet-silverado
 *   2. a sibling we already have a picture of         F-350 -> ford-f250
 *   3. the body type, from the model or description   any van -> a cargo van
 *   4. a plain car                                    so never a coin
 *
 * Steps 3 and 4 are six generated pictures (public/images/cars/generic-*),
 * made 2026-10-14 in the same style as the model pictures: a photoreal
 * front view on a wet lot, 288px square.
 *
 * Pure and shared: the desk's tiles (src/ui/carthumb.ts) and the fleet
 * page's server (server/routes/fleet.ts) both ask this, so a truck has the
 * same face on both.
 */

import { normalizeMake } from "../../public/make-normalize.js";
import { CAR_MODELS } from "../data/car-models.ts";

export type ArtVehicle = {
  make?: string | null; model?: string | null; description?: string | null; trim?: string | null;
} | null | undefined;

const slug = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, "");

/** The six that always exist. Body types, not makes. */
export const GENERIC = {
  pickup: "generic-pickup",
  van: "generic-van",
  boxTruck: "generic-box-truck",
  semi: "generic-semi",
  suv: "generic-suv",
  car: "generic-car",
} as const;

/**
 * SIBLINGS. A model we have no picture of, to one we do that looks the
 * same from the front. Keyed make-slug then model-slug (the whole model,
 * squashed, tried before its first word, so "transitconnect" and
 * "promastercity" can differ from "transit" and "promaster").
 */
const SIBLING: Record<string, Record<string, string>> = {
  ford: {
    f250sd: "ford-f250", f350: "ford-f250", f350sd: "ford-f250", f450: "ford-f250", f550: "ford-f250",
    superduty: "ford-f250", f100: "ford-f150", f150lightning: "ford-f150", lightning: "ford-f150",
    e150: "ford-transit", e250: "ford-transit", e350: "ford-transit", e450: "ford-transit",
    econoline: "ford-transit", eseries: "ford-transit", transitconnect: "ford-transit",
    maverick: "ford-ranger", expeditionmax: "ford-expedition",
  },
  chevrolet: {
    "1500": "chevrolet-silverado", "2500": "chevrolet-silverado", "3500": "chevrolet-silverado",
    "2500hd": "chevrolet-silverado", "3500hd": "chevrolet-silverado", ck: "chevrolet-pickup",
    c1500: "chevrolet-pickup", k1500: "chevrolet-pickup", c2500: "chevrolet-pickup", k2500: "chevrolet-pickup",
    savana: "chevrolet-express", s10: "chevrolet-colorado", blazer: "chevrolet-trailblazer",
    traverse: "chevrolet-tahoe",
  },
  gmc: {
    denali: "gmc-sierra", "1500": "gmc-sierra", "2500": "gmc-sierra", "3500": "gmc-sierra",
    "2500hd": "gmc-sierra", "3500hd": "gmc-sierra", savana: "chevrolet-express",
    canyon: "chevrolet-colorado", yukonxl: "gmc-yukon", envoy: "gmc-terrain", hummer: "hummer-h2",
  },
  ram: {
    "2500": "ram-1500", "3500": "ram-1500", "4500": "ram-1500", "5500": "ram-1500",
    promastercity: "ram-promaster", dakota: "ram-1500",
  },
  dodge: {
    "1500": "dodge-ram", "2500": "dodge-ram", "3500": "dodge-ram", dakota: "dodge-ram",
    sprinter: "mercedesbenz-sprinter", promaster: "ram-promaster",
  },
  mercedesbenz: { metris: "mercedesbenz-sprinter" },
  toyota: { hilux: "toyota-tacoma", t100: "toyota-tacoma" },
  nissan: { titan: "nissan-frontier", xterra: "nissan-pathfinder" },
  freightliner: { sprinter: "mercedesbenz-sprinter" },
  cadillac: { escaladeesv: "cadillac-escalade" },
  lincoln: { navigatorl: "lincoln-navigator" },
};

/** Body types by the words people type for them. Checked in this order. */
const BODY: Array<[RegExp, string]> = [
  [/\b(semi|tractor|cascadia|kenworth|peterbilt|mack|western\s*star|sleeper|day\s*cab)\b/i, GENERIC.semi],
  [/\b(box\s*truck|npr|nqr|nrr|hino|m2|ftr|fvr|cab\s*over|cabover|stake\s*bed|flatbed|dump|bucket|reefer|26\s*ft|16\s*ft|cutaway|step\s*van)\b/i, GENERIC.boxTruck],
  [/\b(van|vans|cargo|sprinter|transit|express|savana|promaster|econoline|econo|metris|nv\d*|e-?\d{3}|city\s*express|caravan|odyssey|oddysey|sienna|pacifica|minivan|shuttle|carnival|sedona|uplander|quest|town\s*(and|&)\s*country)\b/i, GENERIC.van],
  [/\b(pick\s*up|pickup|truck|f-?\d{3}|silverado|sierra|ram|tacoma|tundra|ranger|colorado|canyon|frontier|titan|ridgeline|gladiator|maverick|\d{4}\s*hd|crew\s*cab|regular\s*cab|extended\s*cab|1500|2500|3500)\b/i, GENERIC.pickup],
  [/\b(suv|crossover|tahoe|suburban|yukon|expedition|explorer|4runner|wrangler|cherokee|durango|pilot|highlander|escalade|navigator|bronco|blazer|traverse|4x4|awd|rx\s*\d*|nx\s*\d*|gx|lx|x[1-7]|q[3-8]|xc\d{2}|gle|glk|gls|ml\d*|telluride|palisade|armada|passport|cayenne|discovery|lr\d|range\s*rover|rdx|mdx|trax|crosstrek|outback|mountaineer|montero|rodeo|vitara|juke|venue|hr-?v|cr-?v|flex|element|equinox|terrain|acadia|enclave|envision|encore|santa\s*fe|sorento|sportage|rogue|murano|kicks|rav4|venza|highlander|sequoia|tiguan|atlas|touareg|cx-?\d+|forester|ascent|outlander|edge|escape|expedition|excursion|journey|compass|patriot|renegade|h2|hmmwv|model\s*[xy])\b/i, GENERIC.suv],
];

/** Makes that only build trucks bigger than a pickup. */
const HEAVY_MAKE_BODY: Record<string, string> = {
  freightliner: GENERIC.boxTruck, international: GENERIC.boxTruck, hino: GENERIC.boxTruck,
  isuzu: GENERIC.boxTruck, ud: GENERIC.boxTruck,
  kenworth: GENERIC.semi, peterbilt: GENERIC.semi, mack: GENERIC.semi, westernstar: GENERIC.semi,
};

/**
 * The picture's key (no folder, no extension). Never null: the last answer
 * is the plain car. `exact` says whether it is this make and model, which
 * the desk uses to decide whether to draw it as a stand-in.
 */
export function carArtKey(v: ArtVehicle): { key: string; exact: boolean } {
  const m = normalizeMake(v?.make ?? "");
  const make = m.make ? slug(m.make) : "";
  const rawModel = String(v?.model ?? "").trim();
  const whole = slug(rawModel);
  const first = slug(rawModel.split(/\s+/)[0] ?? "");

  if (make && first && !m.heavyDuty) {
    const exact = `${make}-${first}`;
    if (CAR_MODELS.has(exact)) return { key: exact, exact: true };
  }
  if (make) {
    const sib = SIBLING[make]?.[whole] ?? SIBLING[make]?.[first];
    if (sib && CAR_MODELS.has(sib)) return { key: sib, exact: false };
  }

  const words = [v?.make, rawModel, v?.trim, v?.description].filter(Boolean).join(" ");
  for (const [re, key] of BODY) if (re.test(words)) return { key, exact: false };
  if (make && HEAVY_MAKE_BODY[make]) return { key: HEAVY_MAKE_BODY[make]!, exact: false };
  if (m.heavyDuty) return { key: GENERIC.boxTruck, exact: false };
  return { key: GENERIC.car, exact: false };
}

/** The URL a page puts in an <img>. */
export const carArtUrl = (v: ArtVehicle): string => `/images/cars/${carArtKey(v).key}.jpg`;
